import React, { useRef, useState, useEffect, useCallback } from 'react'

const SCROLL_SHADOW_STYLES = `
.ngn-scroll-shadow { position: relative; width: 100%; }
.ngn-scroll-shadow > .ngn-scroll-x { overflow-x: auto; }
.ngn-scroll-x > table th,
.ngn-scroll-x > table td { min-width: 5rem; }
.ngn-scroll-x > table tr > :first-child { min-width: 9rem; }
.ngn-scroll-shadow::before,
.ngn-scroll-shadow::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 16px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 1;
}
.ngn-scroll-shadow::before { left: 0; background: linear-gradient(to right, var(--shadow), transparent); }
.ngn-scroll-shadow::after { right: 0; background: linear-gradient(to left, var(--shadow), transparent); }
.ngn-scroll-shadow.can-scroll-left::before,
.ngn-scroll-shadow.can-scroll-right::after { opacity: 1; }
`

// Horizontal scroller that shades whichever edge has hidden content, since overlay
// scrollbars (macOS, iOS, Android) stay invisible until the user scrolls.
export const ScrollShadow = ({ label = 'Scrollable table', children }) => {
  const scrollerRef = useRef(null)
  const [edges, setEdges] = useState({ left: false, right: false })

  const update = useCallback(() => {
    const el = scrollerRef.current
    if (!el) return
    const left = el.scrollLeft > 1
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
    setEdges(prev => (prev.left === left && prev.right === right ? prev : { left, right }))
  }, [])

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    update()
    el.addEventListener('scroll', update, { passive: true })
    let observer
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update)
      observer.observe(el)
      if (el.firstElementChild) observer.observe(el.firstElementChild)
    }
    return () => {
      el.removeEventListener('scroll', update)
      observer?.disconnect()
    }
  }, [update])

  const scrollable = edges.left || edges.right
  const classes = ['ngn-scroll-shadow']
  if (edges.left) classes.push('can-scroll-left')
  if (edges.right) classes.push('can-scroll-right')

  return (
    <div className={classes.join(' ')}>
      <style>{SCROLL_SHADOW_STYLES}</style>
      <div
        ref={scrollerRef}
        className="ngn-scroll-x"
        role="region"
        aria-label={label}
        tabIndex={scrollable ? 0 : undefined}
      >
        {children}
      </div>
    </div>
  )
}
